/**
 * useScanBandOnce
 *
 * Read a single NFC card, returning its identifier. Reuses the same pause the
 * WristbandSyncDialog relies on: `nfcConnector.setBusy(true)` makes the global
 * NfcCardScanPopover skip its handler (`if (nfcConnector.isBusy) return`), so
 * our one-shot listener catches the tap instead of the app navigating away.
 * Busy is always released when the scan resolves, times out, or is cancelled.
 *
 * Which identifier you get depends on what you're going to do with it:
 *
 *   - `prefer: 'credential'` (default) — the written credential, falling back
 *     to the raw UID for a never-paired card. Correct for EVERY server lookup,
 *     including the approver endpoints (`approver_nfc_uid` /
 *     `authorizer_nfc_uid`): the backend hashes whatever it is given and
 *     compares against `Wristband.nfc_uid_hash`, and that column is built from
 *     whatever `Wristband.nfc_uid` holds — the written credential for every
 *     Shaka-paired band. The uid fallback covers a legacy never-paired card,
 *     whose stored value IS its chip UID.
 *   - `prefer: 'uid'` — always the raw chip UID. No current caller: it only
 *     matches a band stored by chip UID, and pairing overwrites that with the
 *     credential. The approver dialog used to ask for this, which is why the
 *     wristband factor failed for every paired band with "Authorized wristband
 *     not found" — the hash of a chip UID can never match the hash of a
 *     credential. Kept for a caller that genuinely needs the physical UID
 *     (enrolment / diagnostics), not for lookups.
 */
import { useCallback, useRef, useState } from 'react';

import { nfcConnector, type NfcCardReadPayload } from '@/shared/services/nfcConnectorService';
import { classifyScan } from '@/shared/utils/nfcIntegrity';

const SCAN_TIMEOUT_MS = 20_000;

type CardHandler = (payload: Record<string, unknown>) => void;

export interface ScanBandOnceOptions {
  /** Which identifier to resolve. Defaults to 'credential'. */
  prefer?: 'credential' | 'uid';
}

/** Outcome of a detailed scan: a complete read, a chip that was not read in
 *  full, or no tap within the window. Lets a caller distinguish "try again,
 *  the read failed" from "no band was presented" — never a permission problem. */
export type ScanBandOutcome =
  | { status: 'ok'; uid: string }
  | { status: 'bad_read' }
  | { status: 'timeout' };

export function useScanBandOnce({ prefer = 'credential' }: ScanBandOnceOptions = {}) {
  const [scanning, setScanning] = useState(false);
  const handlerRef = useRef<CardHandler | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (handlerRef.current) {
      nfcConnector.off('nfc-card-read', handlerRef.current);
      handlerRef.current = null;
    }
    nfcConnector.setBusy(false);
    setScanning(false);
  }, []);

  const scanDetailed = useCallback(async (): Promise<ScanBandOutcome> => {
    try {
      await nfcConnector.connect();
    } catch {
      // Treat an unreachable reader as a timeout: nothing was read, and there
      // is nothing for the operator to re-tap yet.
      return { status: 'timeout' };
    }
    // Pause the always-on global scanner while we listen for one read.
    nfcConnector.setBusy(true);
    setScanning(true);

    return new Promise<ScanBandOutcome>((resolve) => {
      const handler: CardHandler = (payload) => {
        const p = payload as unknown as NfcCardReadPayload;
        cleanup();
        // A truncated / empty read is a bad read, not an unregistered band —
        // classify it here instead of sending garbage the server would report
        // back as "not found".
        const outcome = classifyScan({ credential: p.credential, uid: p.uid }, prefer);
        resolve(outcome.status === 'ok' ? { status: 'ok', uid: outcome.identifier } : { status: 'bad_read' });
      };
      handlerRef.current = handler;
      nfcConnector.on('nfc-card-read', handler);
      timerRef.current = setTimeout(() => { cleanup(); resolve({ status: 'timeout' }); }, SCAN_TIMEOUT_MS);
    });
  }, [cleanup, prefer]);

  // Back-compat: callers that only need "an identifier or nothing" (lookup,
  // enrolment, identity match) keep the string|null shape. A bad read collapses
  // to null here — better than forwarding a garbage identifier — while the
  // authorize dialog uses scanDetailed to tell the two apart.
  const scan = useCallback(async (): Promise<string | null> => {
    const outcome = await scanDetailed();
    return outcome.status === 'ok' ? outcome.uid : null;
  }, [scanDetailed]);

  return { scan, scanDetailed, scanning, cancel: cleanup };
}
